import { useEffect, useState } from 'react';
import Reveal from '../../components/Reveal';
import HeroSlideshow from '../../components/HeroSlideshow';
import SafeImage from '../../components/SafeImage';
import './ruaha.css';
import {
  IMAGE_FALLBACK,
  RUAHA_HERO_SLIDES,
  RUAHA_PAGE_IMAGES,
} from '../../data/destinationImages';

import lionFace from '../../assets/images/lion-face.jpg';
import elephantFace from '../../assets/images/elephant-face.jpg';
import eagleFace from '../../assets/images/eagle-face.jpg';
import wilddogFace from '../../assets/images/wilddog-face.jpg';
import rhinoFace from '../../assets/images/rhino-face.jpg';
import giraffeSavanna from '../../assets/images/hero-giraffe.jpg';

const RUAHA_IMAGES = RUAHA_PAGE_IMAGES;

const HERO_STATS = [
  { value: '20,226', label: 'km² of wilderness', delay: 0 },
  { value: '10%', label: 'Of Africa\'s lions', delay: 80 },
  { value: 'Great Ruaha', label: 'River lifeline', delay: 160 },
  { value: '1964', label: 'National park', delay: 240 },
];

const WILDLIFE = [
  {
    animalFace: lionFace,
    title: 'Ruaha Lions',
    text: 'Ruaha supports one of Africa\'s most significant lion populations large prides hunt along riverbanks and open miombo glades.',
    facePosition: 'center 38%',
  },
  {
    animalFace: elephantFace,
    title: 'Elephant Superherds',
    text: 'Thousands of elephants traverse baobab-studded savanna and dry riverbeds among the greatest herds in East Africa.',
    facePosition: 'center 42%',
  },
  {
    animalFace: wilddogFace,
    title: 'African Wild Dogs',
    text: 'Painted wolf packs roam remote woodland tracks Ruaha remains a stronghold for this endangered predator.',
    facePosition: 'center 36%',
  },
  {
    animalFace: rhinoFace,
    title: 'Sable & Roan',
    text: 'Ruaha is one of the few parks where sable and roan antelope still thrive alongside greater and lesser kudu.',
    facePosition: 'center 40%',
  },
  {
    animalFace: giraffeSavanna,
    title: 'Cheetahs & Leopards',
    text: 'Cheetahs hunt open plains while leopards stalk riverine thickets predator diversity rivals anywhere in Tanzania.',
    facePosition: 'center 30%',
  },
  {
    animalFace: eagleFace,
    title: 'Miombo Birdlife',
    text: 'Over 570 bird species from fish eagles on the Great Ruaha to endemic miombo specialists in the woodland canopy.',
    facePosition: 'center 28%',
  },
];

const SEASONS = [
  {
    badge: 'peak',
    title: 'Dry Season',
    months: 'June – October',
    text: 'Wildlife concentrates along the Great Ruaha and shrinking waterholes. Cool mornings, clear skies, and superb predator viewing.',
  },
  {
    badge: 'green',
    title: 'Green Season',
    months: 'November – March',
    text: 'Lush miombo woodland, calving antelope, and peak bird activity fewer camps and exceptional photographic light.',
  },
  {
    badge: 'shoulder',
    title: 'Shoulder Season',
    months: 'April – May',
    text: 'Long rains green the landscape. Some tracks become challenging plan routes carefully with your guide or operator.',
  },
];

const EXPERIENCES = [
  {
    image: RUAHA_IMAGES.river,
    title: 'Great Ruaha River Drives',
    text: 'Follow the park\'s lifeline as elephants, hippos, and crocodiles gather at sand rivers and remaining pools in the dry season.',
  },
  {
    image: RUAHA_IMAGES.baobab,
    title: 'Baobab & Miombo Safari',
    text: 'Wind through ancient baobab groves and vast miombo woodland landscapes that define Ruaha\'s rugged, untamed character.',
  },
  {
    image: RUAHA_IMAGES.savanna,
    title: 'Walking & Fly-in Wilderness',
    text: 'Explore on foot with an armed ranger or fly into remote airstrips for multi-day circuits far from the beaten path.',
  },
];

const GALLERY_IMAGES = [
  { src: RUAHA_IMAGES.galleryLioness, caption: 'Lioness on the Floodplain' },
  { src: RUAHA_IMAGES.galleryElephants, caption: 'Elephants at the Baobab' },
  { src: RUAHA_IMAGES.galleryGiraffe, caption: 'Giraffe Crossing the Track' },
  { src: RUAHA_IMAGES.galleryLeopard, caption: 'Leopard & Cub at Rest' },
  { src: RUAHA_IMAGES.galleryHippos, caption: 'Hippos in the Waterhole' },
  { src: RUAHA_IMAGES.galleryWildDog, caption: 'African Wild Dog' },
];

const SAFARI_PACKAGES = [
  {
    image: RUAHA_IMAGES.packageLodge,
    title: 'Ruaha Fly-In Escape',
    duration: '3–4 days',
    tag: 'Short safari',
    text: 'Fly into a remote camp for quiet game drives through baobab savanna and miombo big cats and elephants without the northern crowds.',
    highlights: ['Airstrip transfers', 'Daily game drives', 'Wilderness camp'],
  },
  {
    image: RUAHA_IMAGES.packageCrocodile,
    title: 'Great Ruaha River Safari',
    duration: '4–5 days',
    tag: 'River focus',
    text: 'Base near the Great Ruaha for dry-season wildlife at the sand rivers elephants, hippos, and predators sharing the riverbed.',
    highlights: ['Sand-river drives', 'Predator viewing', 'Riverside lodges'],
  },
  {
    image: RUAHA_IMAGES.packageJeeps,
    title: 'Walking & Baobab Adventure',
    duration: '5–6 days',
    tag: 'Active safari',
    text: 'Mix vehicle drives with guided walks among ancient baobabs an intimate way to experience Ruaha\'s rugged character on foot.',
    highlights: ['Guided walking', 'Baobab landscapes', 'Armed ranger'],
  },
  {
    image: RUAHA_IMAGES.packageHiking,
    title: 'Southern Circuit Deep South',
    duration: '8–12 days',
    tag: 'Most popular',
    text: 'Combine Ruaha with Nyerere or Mikumi for Tanzania\'s wildest multi-park journey fly-ins, walking, and remote southern lodges.',
    highlights: ['Multi-park fly-ins', 'Lions & wild dogs', 'Private guiding'],
  },
];

const GETTING_THERE_ROUTES = [
  'Fly from Dar es Salaam to Msembe or Jongomero airstrips the most practical way to reach Ruaha',
  'Drive from Iringa (~130 km) on a scenic but rough road allow a full day from Dar via Mikumi if travelling overland',
  'Combine with Mikumi National Park en route from Dar, or link with Nyerere and Udzungwa for a full Southern Circuit',
  'Allow at least 3–4 nights — Ruaha\'s scale and wildlife density reward unhurried game drives and walking safaris',
];

function Lightbox({ image, onClose }) {
  if (!image) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="ruaha-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={handleBackdropClick}
    >
      <button
        className="ruaha-lightbox__close"
        aria-label="Close image viewer"
        onClick={onClose}
      >
        ✕
      </button>
      <img
        className="ruaha-lightbox__img"
        src={image.src}
        alt={image.caption}
      />
      <span className="ruaha-lightbox__caption">{image.caption}</span>
    </div>
  );
}

function Ruaha() {
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="ruaha">
      <section className="ruaha-hero" aria-labelledby="ruaha-hero-heading">
        <div className="ruaha-hero__media" aria-hidden="true">
          <HeroSlideshow classPrefix="ruaha-hero" slides={RUAHA_HERO_SLIDES} fallback={IMAGE_FALLBACK} />
          <div className="ruaha-hero__overlay" />
        </div>
        <div className="page-container ruaha-hero__container">
          <div className="ruaha-hero__content">
            <nav className="ruaha-hero__breadcrumb" aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span className="ruaha-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <a href="#safaris">Southern Circuit</a>
              <span className="ruaha-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <span>Ruaha</span>
            </nav>
            <p className="ruaha-hero__eyebrow">Southern Safari Circuit</p>
            <h1 id="ruaha-hero-heading">
              Ruaha
              <span>National Park</span>
            </h1>
            <p className="ruaha-hero__lead">
              Tanzania&apos;s wild heart vast miombo woodlands, baobab horizons,
              and predator-rich savanna where elephant superherds and lion prides
              rule a park twice the size of many countries.
            </p>
            <div className="ruaha-hero__actions">
              <a className="ruaha-btn ruaha-btn--primary" href="#contact">
                Plan your Ruaha safari
              </a>
              <a className="ruaha-btn ruaha-btn--outline" href="#ruaha-gallery">
                View gallery
              </a>
            </div>
          </div>
        </div>
        <div className="ruaha-hero__stats">
          {HERO_STATS.map((stat) => (
            <div
              key={stat.label}
              className="ruaha-hero__stat"
              style={{ '--stat-delay': `${stat.delay}ms` }}
            >
              <span className="ruaha-hero__stat-value">{stat.value}</span>
              <span className="ruaha-hero__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section
        id="ruaha-overview"
        className="ruaha-section ruaha-overview"
        aria-labelledby="overview-heading"
      >
        <div className="page-container">
          <div className="ruaha-overview__grid">
            <Reveal className="ruaha-overview__text">
              <p className="ruaha-section__eyebrow">About the park</p>
              <h2 id="overview-heading">Where the wild still runs free</h2>
              <p>
                Ruaha National Park covers 20,226 km² of rugged central-southern
                Tanzania one of the largest protected areas in East Africa and
                among the least visited of the country&apos;s great parks. Named
                after the Great Ruaha River that bisects its eastern boundary,
                it is a landscape of baobab giants, golden grassland, and endless
                miombo woodland.
              </p>
              <p>
                What draws safari connoisseurs is the density and diversity of
                wildlife in near-total solitude. Ruaha holds roughly 10% of
                Africa&apos;s remaining lions, enormous elephant herds, strong
                wild dog packs, and rare antelope like sable and roan all
                without the convoy traffic of the Northern Circuit.
              </p>
              <ul className="ruaha-overview__details">
                <Reveal as="li" className="ruaha-overview__detail" delay={0}>
                  <span className="ruaha-overview__detail-label">Location</span>
                  <span className="ruaha-overview__detail-value">Iringa Region, Tanzania</span>
                </Reveal>
                <Reveal as="li" className="ruaha-overview__detail" delay={80}>
                  <span className="ruaha-overview__detail-label">Park size</span>
                  <span className="ruaha-overview__detail-value">20,226 km²</span>
                </Reveal>
                <Reveal as="li" className="ruaha-overview__detail" delay={160}>
                  <span className="ruaha-overview__detail-label">River</span>
                  <span className="ruaha-overview__detail-value">Great Ruaha</span>
                </Reveal>
                <Reveal as="li" className="ruaha-overview__detail" delay={240}>
                  <span className="ruaha-overview__detail-label">Best for</span>
                  <span className="ruaha-overview__detail-value">Lions &amp; elephants</span>
                </Reveal>
              </ul>
            </Reveal>
            <Reveal className="ruaha-overview__image" variant="right" delay={200}>
              <SafeImage
                className="ruaha-overview__img"
                src={RUAHA_IMAGES.overview}
                alt="African elephants in Ruaha National Park"
              />
              <span className="ruaha-overview__image-accent" aria-hidden="true" />
            </Reveal>
          </div>
        </div>
      </section>

      <section
        id="ruaha-packages"
        className="ruaha-section ruaha-packages"
        aria-labelledby="packages-heading"
      >
        <div className="page-container">
          <Reveal className="ruaha-section__header">
            <p className="ruaha-section__eyebrow">Safari packages</p>
            <h2 id="packages-heading">Ruaha safari packages</h2>
            <p className="ruaha-section__intro">
              From a short fly-in escape to a full Southern Circuit journey
              each package is tailored to your dates, camp style, and pace.
            </p>
          </Reveal>
          <ul className="ruaha-packages__grid">
            {SAFARI_PACKAGES.map((pkg, index) => (
              <Reveal
                key={pkg.title}
                as="li"
                className="ruaha-packages__card"
                delay={index * 100}
              >
                <div className="ruaha-packages__card-image">
                  <SafeImage src={pkg.image} alt={pkg.title} />
                  <span className="ruaha-packages__duration">{pkg.duration}</span>
                </div>
                <div className="ruaha-packages__card-body">
                  <span className="ruaha-packages__tag">{pkg.tag}</span>
                  <h3>{pkg.title}</h3>
                  <p>{pkg.text}</p>
                  <ul className="ruaha-packages__highlights">
                    {pkg.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <a className="ruaha-packages__cta" href="#contact">
                    Enquire about this package →
                  </a>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="ruaha-wildlife"
        className="ruaha-section ruaha-wildlife"
        aria-labelledby="wildlife-heading"
      >
        <div className="page-container">
          <Reveal className="ruaha-section__header">
            <p className="ruaha-section__eyebrow">Iconic wildlife</p>
            <h2 id="wildlife-heading">Predators, elephants &amp; rare antelope</h2>
            <p className="ruaha-section__intro">
              From lion prides on the sand rivers to sable in the miombo
              Ruaha delivers big-game drama in one of Africa&apos;s most remote settings.
            </p>
          </Reveal>
          <ul className="ruaha-wildlife__grid">
            {WILDLIFE.map((item, index) => (
              <Reveal
                key={item.title}
                as="li"
                className="ruaha-wildlife__card"
                delay={index * 100}
                variant="scale"
              >
                <div className="ruaha-wildlife__icon-frame">
                  <span className="ruaha-wildlife__icon-ring" aria-hidden="true" />
                  <span
                    className="ruaha-wildlife__icon"
                    style={{ '--wildlife-face-position': item.facePosition }}
                  >
                    <img src={item.animalFace} alt={item.title} />
                  </span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="ruaha-gallery"
        className="ruaha-section ruaha-gallery"
        aria-labelledby="gallery-heading"
      >
        <div className="page-container">
          <Reveal className="ruaha-section__header">
            <p className="ruaha-section__eyebrow">Photo gallery</p>
            <h2 id="gallery-heading">Scenes from Ruaha</h2>
            <p className="ruaha-section__intro">
              From baobab sunsets to riverbank elephant parades the landscapes
              and wildlife of Tanzania&apos;s greatest wilderness park.
            </p>
          </Reveal>
          <div className="ruaha-gallery__grid">
            {GALLERY_IMAGES.map((img, index) => (
              <Reveal
                key={img.caption}
                className="ruaha-gallery__item"
                delay={index * 100}
                variant="scale"
                onClick={() => setLightboxImage(img)}
                role="button"
                tabIndex={0}
                aria-label={`View ${img.caption}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLightboxImage(img);
                  }
                }}
              >
                <SafeImage src={img.src} alt={img.caption} />
                <span className="ruaha-gallery__caption">{img.caption}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}

      <section
        id="ruaha-seasons"
        className="ruaha-section ruaha-seasons"
        aria-labelledby="seasons-heading"
      >
        <div className="page-container">
          <Reveal className="ruaha-section__header">
            <p className="ruaha-section__eyebrow">Plan your timing</p>
            <h2 id="seasons-heading">Best time to visit Ruaha</h2>
            <p className="ruaha-section__intro">
              The dry season draws wildlife to the Great Ruaha but each season
              reveals a different side of this vast park.
            </p>
          </Reveal>
          <ul className="ruaha-seasons__grid">
            {SEASONS.map((season, index) => (
              <Reveal
                key={season.title}
                as="li"
                className="ruaha-seasons__card"
                delay={index * 120}
              >
                <span
                  className={`ruaha-seasons__card-badge ruaha-seasons__card-badge--${season.badge}`}
                >
                  {season.badge === 'peak'
                    ? '★ Peak Season'
                    : season.badge === 'green'
                      ? '🌿 Green Season'
                      : '◐ Shoulder Season'}
                </span>
                <h3>{season.title}</h3>
                <span className="ruaha-seasons__card-months">{season.months}</span>
                <p>{season.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="ruaha-experiences"
        className="ruaha-section ruaha-experiences"
        aria-labelledby="experiences-heading"
      >
        <div className="page-container">
          <Reveal className="ruaha-section__header">
            <p className="ruaha-section__eyebrow">Safari experiences</p>
            <h2 id="experiences-heading">Ways to explore Ruaha</h2>
            <p className="ruaha-section__intro">
              River drives, baobab trails, and walking safaris Ruaha rewards
              travellers who seek depth over crowds.
            </p>
          </Reveal>
          <ul className="ruaha-experiences__grid">
            {EXPERIENCES.map((exp, index) => (
              <Reveal
                key={exp.title}
                as="li"
                className="ruaha-experiences__card"
                delay={index * 120}
              >
                <div className="ruaha-experiences__card-image">
                  <SafeImage src={exp.image} alt={exp.title} />
                </div>
                <div className="ruaha-experiences__card-body">
                  <h3>{exp.title}</h3>
                  <p>{exp.text}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="ruaha-getting-there"
        className="ruaha-section ruaha-getting-there"
        aria-labelledby="getting-there-heading"
      >
        <div className="page-container">
          <div className="ruaha-getting-there__inner">
            <Reveal className="ruaha-getting-there__copy">
              <p className="ruaha-section__eyebrow">Getting there</p>
              <h2 id="getting-there-heading">How to reach Ruaha National Park</h2>
              <p>
                Ruaha lies in the Iringa Region of central-southern Tanzania,
                roughly 130 km west of the town of Iringa. Most visitors fly in
                from Dar es Salaam, though overland routes via Mikumi are possible
                for adventurous travellers.
              </p>
              <ul className="ruaha-getting-there__routes">
                {GETTING_THERE_ROUTES.map((route) => (
                  <li key={route}>{route}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="ruaha-getting-there__map" variant="right" delay={200}>
              <iframe
                title="Ruaha National Park location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d255000!2d34.8!3d-7.5!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x185b8e8c8e8e8e8f%3A0x1234567890abcdef!2sRuaha%20National%20Park!5e0!3m2!1sen!2stz!4v1700000000000!5m2!1sen!2stz"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="ruaha-cta" aria-labelledby="ruaha-cta-heading">
        <div className="page-container">
          <Reveal variant="scale" delay={0}>
            <div className="ruaha-cta__inner">
              <h2 id="ruaha-cta-heading">
                Ready for Tanzania&apos;s wildest park?
              </h2>
              <p>
                Tell us your travel dates we&apos;ll design a tailor-made Ruaha
                safari with river drives, walking safaris, and Southern Circuit
                connections.
              </p>
              <a
                className="ruaha-btn ruaha-btn--primary ruaha-btn--lg"
                href="#contact"
              >
                Get a free Ruaha quote
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Ruaha;
